import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  CompanyBackupImportMode,
  importCompanyBackup,
} from "../../services/companyBackup.service";
import PageToaster from "../toaster/PageToaster";

interface CompanyOption {
  company_id: number;
  company_name: string;
}

interface CompanyImportModalProps {
  open: boolean;
  onClose: () => void;
}

const boxStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(560px, 95vw)",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
  display: "flex",
  flexDirection: "column" as const,
  gap: 2,
};

const CompanyImportModal: React.FC<CompanyImportModalProps> = ({ open, onClose }) => {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
  const [mode, setMode] = useState<CompanyBackupImportMode>("CREATE");
  const [file, setFile] = useState<File | null>(null);
  const [confirmationPhrase, setConfirmationPhrase] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const containerId = "toaster-company-import";
  const { autoSuccess, autoError } = usePageToast(containerId, true);

  useEffect(() => {
    if (!open) return;
    setLoadingCompanies(true);
    api
      .get("/company/admin")
      .then((r) => setCompanies(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCompanies([]))
      .finally(() => setLoadingCompanies(false));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedCompany(null);
      setMode("CREATE");
      setFile(null);
      setConfirmationPhrase("");
    }
  }, [open]);

  const expectedPhrase = selectedCompany
    ? `CONFIRMAR REIMPORTACAO ${selectedCompany.company_name}`
    : "";

  const canSubmit =
    !!selectedCompany &&
    !!file &&
    (mode === "CREATE" || confirmationPhrase === expectedPhrase);

  const handleSubmit = async () => {
    if (!selectedCompany || !file) return;
    setSubmitting(true);
    try {
      const result = await importCompanyBackup(
        selectedCompany.company_id,
        file,
        mode,
        mode === "REPLACE" ? confirmationPhrase : undefined
      );
      const total = Object.values(result.insertedCounts || {}).reduce((a, b) => a + b, 0);
      autoSuccess(
        `Importação concluída: ${total} registros inseridos.` +
          (result.safetySnapshotFile
            ? ` Snapshot de segurança salvo em: ${result.safetySnapshotFile}.`
            : "")
      );
      setFile(null);
      setConfirmationPhrase("");
    } catch (err) {
      autoError(extractErrorMessage(err) || "Falha ao reimportar o backup da empresa.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={boxStyle}>
        <Typography variant="h6" component="h2">
          Reimportar backup de empresa
        </Typography>

        <Autocomplete
          options={companies}
          getOptionLabel={(opt) => opt.company_name || ""}
          value={selectedCompany}
          isOptionEqualToValue={(option, value) => option?.company_id === value?.company_id}
          onChange={(_, v) => setSelectedCompany(v)}
          disabled={submitting}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Empresa de destino"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingCompanies && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <FormControl fullWidth>
          <InputLabel id="import-mode-label">Modo</InputLabel>
          <Select
            labelId="import-mode-label"
            label="Modo"
            value={mode}
            onChange={(e) => setMode(e.target.value as CompanyBackupImportMode)}
            disabled={submitting}
          >
            <MenuItem value="CREATE">Apenas inserir (falha se já houver dados)</MenuItem>
            <MenuItem value="REPLACE">Recriar do zero (apaga os dados atuais da empresa)</MenuItem>
          </Select>
        </FormControl>

        <Button variant="outlined" component="label" disabled={submitting}>
          {file ? file.name : "Selecionar arquivo .json"}
          <input
            type="file"
            accept="application/json"
            hidden
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Button>

        {mode === "REPLACE" && selectedCompany && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography color="error" variant="body2">
              Isso apaga TODOS os dados atuais de "{selectedCompany.company_name}" antes de
              inserir os dados do arquivo. Um snapshot de segurança é salvo automaticamente antes
              de apagar. Digite exatamente para confirmar:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {expectedPhrase}
            </Typography>
            <TextField
              size="small"
              value={confirmationPhrase}
              onChange={(e) => setConfirmationPhrase(e.target.value)}
              placeholder="Digite a frase de confirmação"
              disabled={submitting}
            />
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={onClose} disabled={submitting}>
            Fechar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "Importando..." : "Importar"}
          </Button>
        </Box>

        <PageToaster containerId={containerId} />
      </Box>
    </Modal>
  );
};

export default CompanyImportModal;
